import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Drawer } from '@mui/material'
import { StyledButton } from './ImportFromInventory.styles.ts'
import { useAddProductsFromInventory } from '@/data/replenishmentPlan/hooks/useAddProductsFromInventory.ts'
import { useGetInventoryProductsForImport } from '@/data/replenishmentPlan/hooks/useGetInventoryProductsForImport.ts'
import { useFetchAndSavePlan } from '@/data/replenishmentPlan/hooks/useFetchAndSavePlan.ts'
import ImportFromInventoryDrawer from './ImportFromInventoryDrawer.tsx'
import { Product } from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import { usePlanDrawerContainer } from '@/ui/screens/ReplenishmentPlan/PlanDrawerContainerContext.ts'
import NoTransition from '@/ui/screens/ReplenishmentPlan/NoTransition.tsx'

interface SearchParams {
  term: string
  divisionId?: number
}

const ImportFromInventory = () => {
  const { t } = useTranslation()
  const drawerContainer = usePlanDrawerContainer()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const searchParamsRef = useRef<SearchParams>({ term: '' })

  const { addProductsFromInventory, isLoading: isAddingProduct } =
    useAddProductsFromInventory()
  const { fetchProducts, products, divisions, isLoading } =
    useGetInventoryProductsForImport()
  const { fetchAndSavePlan } = useFetchAndSavePlan()

  const handleClick = () => {
    setIsDrawerOpen(true)
  }

  const handleClose = () => {
    setIsDrawerOpen(false)
  }

  const handleSearch = async (term: string, divisionId?: number) => {
    searchParamsRef.current = { term, divisionId }
    await fetchProducts(term, divisionId)
  }

  const handleAddProduct = async (product: Product) => {
    try {
      await addProductsFromInventory([product.id])
      await fetchAndSavePlan()
      const { term, divisionId } = searchParamsRef.current
      await fetchProducts(term, divisionId)
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <>
      <StyledButton variant="outlined" onClick={handleClick}>
        {t('importFromInventory.button')}
      </StyledButton>

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleClose}
        TransitionComponent={NoTransition}
        container={drawerContainer}
        ModalProps={{ style: { position: 'absolute' } }}
        PaperProps={{ style: { position: 'absolute' } }}
      >
        <ImportFromInventoryDrawer
          divisions={divisions}
          products={products}
          isLoading={isLoading}
          isAddingProduct={isAddingProduct}
          onClose={handleClose}
          onSearch={handleSearch}
          onAddProduct={handleAddProduct}
        />
      </Drawer>
    </>
  )
}

export default ImportFromInventory
